import React from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, ContactShadows, RoundedBox, Text } from "@react-three/drei";

type PieceColor = "w" | "b";
type PieceType = "p" | "r" | "n" | "b" | "q" | "k";

const back: PieceType[] = ["r", "n", "b", "q", "k", "b", "n", "r"];
const icon: Record<PieceType, string> = { p: "P", r: "R", n: "N", b: "B", q: "Q", k: "K" };

function PieceMaterial({ color }: { color: PieceColor }) {
  const white = color === "w";
  return (
    <meshPhysicalMaterial
      color={white ? "#fff1d4" : "#020308"}
      metalness={white ? 0.45 : 0.95}
      roughness={white ? 0.16 : 0.08}
      clearcoat={1}
      clearcoatRoughness={0.04}
      emissive={white ? "#8a5c10" : "#00eaff"}
      emissiveIntensity={white ? 0.04 : 0.35}
    />
  );
}

function LuxuryPiece({ type, color, x, z }: { type: PieceType; color: PieceColor; x: number; z: number }) {
  const crownY = type === "p" ? 1.45 : type === "k" ? 2.25 : type === "q" ? 2.1 : 1.9;
  const bodyH = type === "p" ? 0.85 : type === "k" ? 1.45 : type === "q" ? 1.32 : 1.15;
  const glow = color === "w" ? "#ffd77a" : "#00eaff";

  return (
    <group position={[x, 0.38, z]} scale={[1.05, 1.05, 1.05]}>
      <mesh castShadow position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.56, 0.72, 0.24, 80]} />
        <PieceMaterial color={color} />
      </mesh>

      <mesh castShadow position={[0, 0.34, 0]}>
        <torusGeometry args={[0.42, 0.07, 18, 80]} />
        <PieceMaterial color={color} />
      </mesh>

      <mesh castShadow position={[0, 0.68, 0]}>
        <cylinderGeometry args={[0.34, 0.48, 0.52, 80]} />
        <PieceMaterial color={color} />
      </mesh>

      <mesh castShadow position={[0, 0.95 + bodyH / 2, 0]}>
        <cylinderGeometry args={[0.25, 0.36, bodyH, 80]} />
        <PieceMaterial color={color} />
      </mesh>

      <mesh castShadow position={[0, crownY, 0]}>
        {type === "n" ? <boxGeometry args={[0.55, 0.82, 0.42]} /> : <sphereGeometry args={[type === "p" ? 0.34 : 0.46, 80, 80]} />}
        <PieceMaterial color={color} />
      </mesh>

      {type === "r" && (
        <group position={[0, crownY + 0.36, 0]}>
          <mesh castShadow>
            <cylinderGeometry args={[0.5, 0.5, 0.28, 8]} />
            <PieceMaterial color={color} />
          </mesh>
          {[0, 1, 2, 3].map((i) => (
            <mesh key={i} castShadow position={[Math.cos(i * Math.PI / 2) * 0.36, 0.22, Math.sin(i * Math.PI / 2) * 0.36]}>
              <boxGeometry args={[0.22, 0.24, 0.22]} />
              <PieceMaterial color={color} />
            </mesh>
          ))}
        </group>
      )}

      {type === "b" && (
        <mesh castShadow position={[0, crownY + 0.52, 0]}>
          <coneGeometry args={[0.25, 0.7, 80]} />
          <PieceMaterial color={color} />
        </mesh>
      )}

      {type === "q" && (
        <group position={[0, crownY + 0.45, 0]}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <mesh key={i} castShadow position={[Math.cos(i * Math.PI / 3.5) * 0.38, 0.18, Math.sin(i * Math.PI / 3.5) * 0.38]}>
              <sphereGeometry args={[0.13, 30, 30]} />
              <PieceMaterial color={color} />
            </mesh>
          ))}
        </group>
      )}

      {type === "k" && (
        <group position={[0, crownY + 0.58, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.12, 0.8, 0.12]} />
            <PieceMaterial color={color} />
          </mesh>
          <mesh castShadow position={[0, 0.22, 0]}>
            <boxGeometry args={[0.55, 0.12, 0.12]} />
            <PieceMaterial color={color} />
          </mesh>
        </group>
      )}

      <Text position={[0, 0.22, 0.75]} rotation={[-0.15, 0, 0]} fontSize={0.32} color={glow} anchorX="center">
        {icon[type]}
      </Text>
    </group>
  );
}

function Scene() {
  const pieces: React.ReactNode[] = [];

  for (let i = 0; i < 8; i++) {
    pieces.push(<LuxuryPiece key={`bp-${i}`} type="p" color="b" x={i - 3.5} z={-2.5} />);
    pieces.push(<LuxuryPiece key={`wp-${i}`} type="p" color="w" x={i - 3.5} z={2.5} />);
    pieces.push(<LuxuryPiece key={`bb-${i}`} type={back[i]} color="b" x={i - 3.5} z={-3.5} />);
    pieces.push(<LuxuryPiece key={`wb-${i}`} type={back[i]} color="w" x={i - 3.5} z={3.5} />);
  }

  return (
    <group rotation={[0, -0.62, 0]} position={[0, -0.45, 0]}>
      <RoundedBox receiveShadow castShadow args={[11.4, 0.85, 11.4]} radius={0.28} smoothness={8} position={[0, -0.28, 0]}>
        <meshPhysicalMaterial color="#01040a" metalness={0.95} roughness={0.06} clearcoat={1} emissive="#001c24" emissiveIntensity={0.3} />
      </RoundedBox>

      {Array.from({ length: 8 }).map((_, r) =>
        Array.from({ length: 8 }).map((_, c) => {
          const dark = (r + c) % 2 === 1;
          return (
            <RoundedBox key={`${r}-${c}`} receiveShadow castShadow args={[1.02, 0.22, 1.02]} radius={0.07} smoothness={4} position={[c - 3.5, 0.28, r - 3.5]}>
              <meshPhysicalMaterial
                color={dark ? "#031421" : "#e9ce86"}
                metalness={dark ? 0.95 : 0.55}
                roughness={0.07}
                clearcoat={1}
                emissive={dark ? "#00eaff" : "#6d4900"}
                emissiveIntensity={dark ? 0.18 : 0.04}
              />
            </RoundedBox>
          );
        })
      )}

      <RoundedBox args={[10.2, 0.16, 0.18]} radius={0.08} position={[0, 0.55, -4.85]}>
        <meshStandardMaterial color="#00eaff" emissive="#00eaff" emissiveIntensity={2.2} />
      </RoundedBox>
      <RoundedBox args={[10.2, 0.16, 0.18]} radius={0.08} position={[0, 0.55, 4.85]}>
        <meshStandardMaterial color="#00eaff" emissive="#00eaff" emissiveIntensity={2.2} />
      </RoundedBox>
      <RoundedBox args={[0.18, 0.16, 10.2]} radius={0.08} position={[-4.85, 0.55, 0]}>
        <meshStandardMaterial color="#00eaff" emissive="#00eaff" emissiveIntensity={2.2} />
      </RoundedBox>
      <RoundedBox args={[0.18, 0.16, 10.2]} radius={0.08} position={[4.85, 0.55, 0]}>
        <meshStandardMaterial color="#00eaff" emissive="#00eaff" emissiveIntensity={2.2} />
      </RoundedBox>

      {pieces}

      <Text position={[0, 0.75, -5.55]} rotation={[-Math.PI / 2, 0, 0]} fontSize={0.42} color="#00eaff" anchorX="center">
        AVAAIO LUXURY V4
      </Text>
    </group>
  );
}

export default function Chess3DBoard() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", background: "radial-gradient(circle at top, #172d48 0%, #05070d 52%, #000 100%)", color: "white" }}>
      <div style={{ padding: "16px 18px 0", maxWidth: 1220, margin: "0 auto" }}>
        <button onClick={() => (window.location.href = "/games")} style={{ padding: "10px 14px", borderRadius: 14, border: "1px solid rgba(0,234,255,.6)", background: "rgba(0,0,0,.55)", color: "#dff", cursor: "pointer" }}>
          ← Back to Games
        </button>
        <h1 style={{ margin: "14px 0 4px", fontSize: 36 }}>AVAAIO Luxury 3D Chess — V4</h1>
        <p style={{ margin: 0, color: "#aefaff" }}>Unmistakable rebuild: rounded board, giant pieces, side camera, neon rails, and premium materials.</p>
      </div>

      <div style={{ height: "82vh", maxWidth: 1280, margin: "14px auto", borderRadius: 34, overflow: "hidden", border: "1px solid rgba(0,234,255,.34)", boxShadow: "0 0 95px rgba(0,234,255,.24)" }}>
        <Canvas shadows camera={{ position: [8.9, 5.7, 9.8], fov: 32 }}>
          <ambientLight intensity={0.38} />
          <directionalLight position={[5, 10, 7]} intensity={4.1} castShadow />
          <pointLight position={[-5, 4, -5]} intensity={4.4} color="#00eaff" />
          <pointLight position={[5, 4, 5]} intensity={2.2} color="#ffd27a" />
          <spotLight position={[0, 10, 0]} angle={0.48} penumbra={0.8} intensity={3.2} castShadow />
          <Environment preset="city" />
          <Scene />
          <ContactShadows position={[0, -1.1, 0]} opacity={0.9} scale={16} blur={2.8} far={9} />
          <OrbitControls enablePan={false} minDistance={5.5} maxDistance={14} maxPolarAngle={Math.PI / 2.02} />
        </Canvas>
      </div>
    </div>
  );
}
